/**
 * A RÉGUA DO CHECKLIST — a MESMA que a rota usa para recusar o diário.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════
 * POR QUE ESTE ARQUIVO EXISTE (e por que ele não é `rdoUtils.checklistPendente`)
 * ════════════════════════════════════════════════════════════════════════════════════════
 *
 * A tela do RDO passou a BARRAR o diário enquanto o checklist tivesse item em aberto — e
 * usou `rdoUtils.checklistPendente`, cuja régua é `obrigatorio !== false && !concluido &&
 * !nao_aplica`. Ela só se satisfaz com `concluido` ou `nao_aplica`, e **quem RESPONDE o item
 * pela tela do RDO não produz nenhum dos dois**:
 *
 *   - `components/rdo/ChecklistItemRDO.js` NUNCA manda `concluido` — os `onChange` dele
 *     mandam `fotos`, `resposta`, `observacao`, `nao_aplica` e `justificativa`. O único
 *     escritor de `concluido` em todo o `src` é a `ChecklistPage` (`/checklist/<id>`), que o
 *     perfil de campo não abre;
 *   - o `nao_aplica` **é** alcançável desde 2026-08-12 (o campo do motivo abre por estado
 *     LOCAL e o PATCH leva os dois campos juntos — `utils/naoAplicaChecklist`), mas ele não é
 *     resposta: é a saída do item que NÃO SE APLICA àquela obra. O item que se aplica, foi
 *     conferido e foi respondido continua sem satisfazer aquela régua, e marcar N/A nele para
 *     destravar a tela seria mentir no registro que o cliente assina.
 *
 * As duas metades estão presas ao componente REAL em `useRDOPage.regua.test.js`
 * ("a prosa da régua, prendida ao componente que ela descreve") — este parágrafo já foi falso
 * uma vez, entre a correção do N/A e a reescrita dele.
 *
 * Resultado medido pelo cético, com a régua antiga: o colaborador respondia tudo, anexava
 * tudo, e `checklistBarraODiario` continuava `true` PARA SEMPRE — tela sem formulário, sem
 * upload, sem assinatura, lendo "assim que os itens obrigatórios estiverem respondidos o
 * formulário aparece aqui". Responder nunca fazia aparecer. Era pior que o defeito original.
 *
 * E o avesso, na mesma queixa: item LEGADO com `obrigatorio:true, concluido:true,
 * resposta:""` — a home (que usa a régua do BACKEND) dizia PENDENTE, a tela NÃO barrava, a
 * pessoa preenchia o diário inteiro e o `POST /rdos` devolvia 400. Duas réguas para a mesma
 * pergunta, e o dono no meio.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════
 * A REGRA, e ela é uma tradução LINHA A LINHA de `_validar_item_checklist`
 * (`backend/routes/checklists_common.py:70-149`), sem o `exigir_conteudo_para_concluir`
 * ════════════════════════════════════════════════════════════════════════════════════════
 *
 * É essa a régua que `validar_checklists_demanda` aplica no `POST /rdos` e no `PUT /rdos`, e a
 * que `mobile_demandas_checklist.marcar_checklist_pendente` aplica no banner da home. Traduzida aqui
 * porque o JS não roda Python — e mantida COLADA nela de propósito: qualquer divergência volta
 * a produzir "a tela deixa e a rota recusa" (ou o contrário), que é o relato do dono.
 *
 * Duas diferenças que NÃO são descuido, e sim a régua do backend:
 *   - `concluido` NÃO entra. O backend nunca perguntou por ele: quem responde e anexa
 *     satisfaz o item. É isto que devolve a saída ao campo;
 *   - item SEM a chave `obrigatorio` NÃO é obrigatório (`item.get("obrigatorio", False)`).
 *     A régua antiga do frontend (`obrigatorio !== false`) tratava a ausência como obrigação,
 *     então ela barrava a tela num item que a rota ACEITA.
 */

const desc = (item) => String(item?.descricao || '').slice(0, 60);

/**
 * ISTO É UM NÚMERO PARA O `float()` DO PYTHON? — a linha que NÃO era tradução nenhuma.
 *
 * A régua do `tipo_resposta: 'numero'` era `Number.isNaN(Number(resposta))`, e `Number()` e
 * `float()` leem gramáticas DIFERENTES. Medido em 2026-08-13 rodando a mesma matriz de 27
 * respostas pelas duas (o Python pelo `_validar_item_checklist` real): **10 divergências**, nos
 * DOIS sentidos —
 *
 *   - `0x10`, `0b101`, `0o17`: `Number('0x10') === 16` e `float('0x10')` levanta `ValueError`.
 *     A tela deixava passar e o `POST /rdos` devolvia 400.
 *   - `nan`, `NaN`, `inf`, `infinity`, `-inf`, `1_0`, `1e1_0`: `float()` aceita todos e
 *     `Number()` devolve `NaN` em todos. A tela escondia o formulário e a home dizia que não
 *     faltava nada — o "a tela deixa e a rota recusa" ao contrário, que é o mesmo relato.
 *
 * A GRAMÁTICA ABAIXO É A DE `float()`, e não uma versão "mais certa" dela:
 *
 *     [sinal] ( inf | infinity | nan | dígitos[.[dígitos]][expoente] | .dígitos[expoente] )
 *     dígitos ::= d (_? d)*        — o `_` só ENTRE dígitos: `1_0` vale, `1__0`/`_1`/`1_` não
 *
 * `nan` E `inf` FORAM RECUSADOS NOS DOIS LADOS EM 2026-08-13, e a ordem importou.
 *
 * Numa primeira passada esta régua foi AFROUXADA para aceitá-los, porque a rota os gravava e
 * apertar só a tela deixaria o campo sem formulário por um documento que o servidor aceita — o
 * defeito pior dos dois. Depois `checklists_common.py::_validar_item_checklist` passou a recusar
 * `float` não-finito (`math.isfinite`), e só ENTÃO esta régua pôde apertar junto. Endurecer a tela
 * antes da rota teria travado o campo; endurecer a rota e esquecer a tela devolveria a divergência
 * pelo outro lado. As duas mudam juntas ou não mudam.
 *
 * A razão de recusar: checklist é MEDIÇÃO (torque, tensão, distância). "nan" não é uma medição
 * ruim — é a ausência de medição se passando por uma, e a jusante vira média, soma e gráfico sem
 * ninguém perceber.
 *
 * `\p{Nd}` e não `\d`: `float()` converte QUALQUER dígito decimal Unicode
 * (`float('١٢٣') === 123.0`), e `\d` é só ASCII — seria uma divergência criada pelo conserto.
 */
const DIG = '\\p{Nd}(?:_?\\p{Nd})*';
const NUMERO_DO_PYTHON = new RegExp(
  `^[+-]?(?:${DIG}(?:\\.(?:${DIG})?)?|\\.${DIG})(?:[eE][+-]?${DIG})?$`,
  'iu',
);

/**
 * ESTA ENTRADA DE `fotos[]` APONTA PARA UM ARQUIVO? — tradução de
 * `checklists_common._tem_arquivo` (`backend/routes/checklists_common.py`).
 *
 * A contagem era `it.fotos.length`, dos dois lados. Medido por um cético (2026-08-12) pela ROTA
 * real: `{concluido: true, fotos: [{}, {}, {}]}` num item `min_fotos: 3` devolvia HTTP 200 com
 * `concluido_em` carimbado e progresso 100% — e a régua daqui, contando igual, liberava o diário
 * pelo mesmo motivo. Três objetos vazios não são três fotos.
 *
 * `url` / `data` / `base64` / `src` são os nomes que o repo de fato usa (`ChecklistItemView`,
 * `checklistParaRdo`), e a string crua cobre o documento legado. Só o que não tem origem
 * nenhuma deixa de contar.
 */
export const fotoTemArquivo = (foto) => {
  if (typeof foto === 'string') return !!foto.trim();
  if (!foto || typeof foto !== 'object') return false;
  return ['url', 'data', 'base64', 'src'].some((k) => String(foto[k] ?? '').trim());
};

/** Quantas fotos o item TEM DE VERDADE — inclusive o par legado `foto_data`/`foto_url`. */
export const contarFotos = (item) => {
  const lista = Array.isArray(item?.fotos) ? item.fotos : [];
  const num = lista.filter(fotoTemArquivo).length;
  return (item?.foto_data || item?.foto_url) ? Math.max(num, 1) : num;
};

/**
 * Este item IMPEDE o diário? Devolve `null` (ok) ou a frase do erro — as MESMAS frases da
 * rota, para o que a tela diz e o que o 400 diz não serem dois textos diferentes.
 */
export const pendenciaDoItem = (item) => {
  const it = item || {};

  // N/A JUSTIFICADO É A SAÍDA LEGÍTIMA, e vem antes de tudo — inclusive antes da isenção do
  // item não-obrigatório, porque N/A sem motivo não pode passar em item nenhum.
  if (it.nao_aplica) {
    return String(it.justificativa || '').trim() ? null : `Justifique o N/A: '${desc(it)}'`;
  }

  // `item.get("obrigatorio", False)` — a ausência da chave NÃO é obrigação.
  if (!it.obrigatorio) return null;

  const tipo = it.tipo_resposta || 'texto';
  const resposta = String(it.resposta ?? '').trim();

  // Fotos podem estar em `fotos[]` (nova) ou em `foto_data`/`foto_url` (legado). A conta é
  // `contarFotos`, e não `fotos.length`: ver `fotoTemArquivo` acima.
  const numFotos = contarFotos(it);
  const minFotos = parseInt(it.min_fotos || 0, 10) || 0;
  const requerFoto = !!it.requer_foto;

  if (tipo === 'sim_nao') {
    if (!['sim', 'nao'].includes(resposta)) return `Responda Sim/Não: '${desc(it)}'`;
  } else if (tipo === 'numero') {
    if (!resposta) return `Número obrigatório: '${desc(it)}'`;
    // `NUMERO_DO_PYTHON` (topo do arquivo) e NÃO `Number()`: as duas gramáticas divergiam em 10
    // dos 27 casos medidos, e a régua daqui existe para responder igual à da rota.
    if (!NUMERO_DO_PYTHON.test(resposta)) return `Valor numérico inválido: '${desc(it)}'`;
  } else if (!resposta && !requerFoto) {
    // texto: sem foto exigida e sem resposta → erro. Com foto exigida, a foto responde.
    return `Resposta obrigatória: '${desc(it)}'`;
  }

  if (requerFoto || minFotos > 0) {
    const exig = Math.max(minFotos, requerFoto ? 1 : 0);
    if (numFotos < exig) return `Anexe pelo menos ${exig} foto(s)/vídeo(s): '${desc(it)}'`;
  }

  // SUBATIVIDADE OBRIGATÓRIA — tradução LINHA A LINHA de `_validar_item_checklist`
  // (`backend/routes/checklists_common.py`), ligada em 2026-08-18 (dono: "ligar só para OS
  // novas"). No MESMO ponto (dentro do ramo obrigatório, depois de N/A e da isenção do item
  // opcional) e com a MESMA frase que o 400 devolve — senão volta o "a tela deixa e a rota
  // recusa" que é o relato do dono. "Só para OS novas" cai de graça: subitem de OS antiga é
  // string pura (`obrigatorio` ausente), e `=== true` não dispara nele.
  // "Respondida" = resposta OU foto real carimbada com o `subitem_id` (`fotoTemArquivo`, e não
  // `length`: `[{}]` não é foto — a mesma conta do item).
  for (const sub of (it.subitens || [])) {
    if (sub?.obrigatorio !== true) continue;
    const resp = String(sub.resposta ?? '').trim();
    const temFoto = (Array.isArray(it.fotos) ? it.fotos : [])
      .some((f) => f && f.subitem_id === sub.id && fotoTemArquivo(f));
    if (!resp && !temFoto) {
      return `Responda a subatividade obrigatória: '${String(sub.texto || '').slice(0, 60)}'`;
    }
  }
  return null;
};

/** Todas as pendências dos checklists da OS: `[{ descricao, erro }]`, na ordem da tela. */
export const pendenciasDoChecklist = (checklists) => {
  const fora = [];
  for (const cl of checklists || []) {
    for (const item of (cl?.itens || [])) {
      const erro = pendenciaDoItem(item);
      if (erro) fora.push({ descricao: item?.descricao || '', erro });
    }
  }
  return fora;
};

/**
 * "O checklist ainda barra o diário?" — a pergunta que a tela e a rota agora fazem igual.
 *
 * LISTA VAZIA DEVOLVE `false`, E ISSO NÃO SIGNIFICA "PODE PREENCHER". Esta função responde
 * sobre UMA LISTA; "esta OS exige checklist e não veio checklist nenhum" é outra pergunta, e ela
 * precisa da demanda (`checklist_template_id`), que aqui não existe. Era exatamente por essa
 * fresta que a tela do RDO liberava o formulário inteiro numa OS cujo modelo foi apagado — o
 * `POST /rdos` recusa com 400 e não há onde responder. Quem faz a pergunta certa é
 * `checklistExigidoSemLista`, em `useRDOPage.js`; lá está a medição.
 */
export const checklistBarraDiario = (checklists) => pendenciasDoChecklist(checklists).length > 0;
